'use client'

import { Card } from '@acko/card'
import { Separator } from '@acko/separator'
import { Typography } from '@acko/typography'
import Image from 'next/image'
import React, { useEffect, useRef } from 'react'
import styles from '../page.module.css'
import {
  buyNewSteps,
  claimSteps,
  imgVerification,
  renewSteps,
  type ProcessStep,
  videoClaimProcess,
  videoRenewProcess,
  videoPurchaseProcess,
} from './data'

const PLAY_THRESHOLD = 0.3

const ProcessStepsSection = ({
  id,
  title,
  steps,
  videoSrc,
  footer,
}: {
  id: string
  title: string
  steps: ProcessStep[]
  videoSrc: string
  footer?: React.ReactNode
}) => {
  const frameRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const frame = frameRef.current
    const video = videoRef.current
    if (!frame || !video) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD) {
          void video.play().catch(() => undefined)
        } else {
          video.pause()
        }
      },
      { threshold: PLAY_THRESHOLD }
    )

    observer.observe(frame)

    return () => {
      observer.disconnect()
      video.pause()
    }
  }, [])

  const processCard = (
    <Card variant="primary" className={styles.renewalSplitShell}>
      <div className={`${styles.renewalSplitSteps} flex flex-col`}>
        {steps.map((step, i, arr) => (
          <div key={step.num}>
            <div className="flex gap-20 items-start py-24">
              <Typography
                scale="4xl"
                emphasis="bold"
                as="span"
                color="brand"
                className={`${styles.stepIndex} text-center`}>
                {step.num}
              </Typography>
              <div className="flex flex-col gap-8 min-w-0">
                <Typography scale="xl" emphasis="medium" as="h3" color="primary">
                  {step.title}
                </Typography>
                <Typography scale="base" color="secondary">
                  {step.desc}
                </Typography>
              </div>
            </div>
            {i < arr.length - 1 && <Separator className="bg-[var(--color-border-subtle)]" decorative />}
          </div>
        ))}
      </div>
      <div className={`${styles.renewalSplitVisual} ${styles.processVideoVisual}`}>
        <div ref={frameRef} className={styles.claimVideoFrame}>
          <video
            ref={videoRef}
            className={styles.renewalPhoneImage}
            src={videoSrc}
            muted
            loop
            playsInline
            preload="metadata"
          />
        </div>
      </div>
    </Card>
  )

  return (
    <div id={id} className="flex flex-col gap-32 lg:gap-40 w-full scroll-mt-[104px]">
      <Typography scale="4xl" emphasis="bold" as="h2" color="primary">
        {title}
      </Typography>
      {footer ? (
        <div className="flex flex-col gap-24">
          {processCard}
          {footer}
        </div>
      ) : (
        processCard
      )}
    </div>
  )
}

export const BuyNewSection = () => (
  <ProcessStepsSection
    id="buy-new"
    title="How to buy comprehensive bike insurance on ACKO"
    steps={buyNewSteps}
    videoSrc={videoPurchaseProcess}
  />
)

export const RenewSection = () => (
  <ProcessStepsSection
    id="renew-bike"
    title="How to renew your ACKO comprehensive bike insurance"
    steps={renewSteps}
    videoSrc={videoRenewProcess}
  />
)

export const ClaimsProcessSection = () => (
  <ProcessStepsSection
    id="file-claim"
    title="How to file a comprehensive bike insurance claim at ACKO"
    steps={claimSteps}
    videoSrc={videoClaimProcess}
    footer={
      <Card variant="primary" className="p-20 md:p-24 lg:p-24 flex items-center gap-16">
        <div className="flex gap-16 items-center">
          <Image
            src={imgVerification}
            alt=""
            className="w-48 h-48 rounded-full object-cover shrink-0"
            width={48}
            height={48}
          />
          <div className="flex flex-col gap-4">
            <Typography scale="xl" emphasis="medium" as="span" color="primary">
              Claim process verified by Amit Desai
            </Typography>
            <Typography scale="base" color="secondary">
              Head of claims operations
            </Typography>
          </div>
        </div>
      </Card>
    }
  />
)
